import React, { useState, useRef, useEffect, useCallback } from 'react';

const INTRO_VIDEO_PATH = '/api/files/reggae-essences/videos/0d80d399-1bcf-40e1-b09d-933d004d28d0.mp4';
const API = process.env.REACT_APP_BACKEND_URL;

export const IntroVideo = ({ onComplete }) => {
  const [phase, setPhase] = useState('playing');
  const [videoReady, setVideoReady] = useState(false);
  const videoRef = useRef(null);
  const fadeStarted = useRef(false);

  const startFadeOut = useCallback(() => {
    if (fadeStarted.current) return;
    fadeStarted.current = true;
    setPhase('fading');
    setTimeout(() => {
      setPhase('done');
      onComplete();
    }, 1200);
  }, [onComplete]);

  useEffect(() => {
    // Safety timeout — if video doesn't play within 6s, fade out
    const timeout = setTimeout(startFadeOut, 6000);
    return () => clearTimeout(timeout);
  }, [startFadeOut]);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    const tryPlay = async () => {
      try {
        await video.play();
        setVideoReady(true);
      } catch {
        // Autoplay blocked — skip immediately
        startFadeOut();
      }
    };

    if (video.readyState >= 2) {
      tryPlay();
    } else {
      video.addEventListener('canplay', tryPlay, { once: true });
    }

    // If video errors out (network, format), skip
    video.addEventListener('error', startFadeOut, { once: true });
  }, [startFadeOut]);

  if (phase === 'done') return null;

  return (
    <div
      data-testid="intro-video-overlay"
      onClick={startFadeOut}
      className="fixed inset-0 z-[9999] bg-black flex items-center justify-center cursor-pointer"
      style={{
        opacity: phase === 'fading' ? 0 : 1,
        transition: 'opacity 1.2s ease-out',
      }}
    >
      <video
        ref={videoRef}
        src={`${API}${INTRO_VIDEO_PATH}`}
        muted
        playsInline
        preload="auto"
        onEnded={startFadeOut}
        className="w-full h-full object-cover"
        style={{ opacity: videoReady ? 1 : 0, transition: 'opacity 0.3s ease-in' }}
      />
      <button
        data-testid="intro-skip-btn"
        onClick={(e) => { e.stopPropagation(); startFadeOut(); }}
        className="absolute bottom-8 right-8 text-white/60 hover:text-white text-sm tracking-widest uppercase transition-colors duration-300"
        style={{ fontFamily: 'monospace' }}
      >
        Skip
      </button>
    </div>
  );
};
